// src/services/interventionEngine.ts
//
// Deterministic, explainable intervention plan engine.
// Pure function — no side effects, no API calls, no Freedom dependency.
// Works with data already loaded from existing RTK Query hooks.

import type {
  Goal,
  Task,
  ManagementSummary,
  TeamRebalancingResult,
  Project,
} from '../types/api';

export type InterventionType = 'reassign' | 'estimate' | 'unblock' | 'defer' | 'focus' | 'review';
export type InterventionSeverity = 'high' | 'medium' | 'low';

export interface Intervention {
  id: string;
  type: InterventionType;
  severity: InterventionSeverity;
  title: string;
  description: string;
  reason: string;
  relatedGoalId?: string;
  relatedTaskId?: string;
  relatedProjectId?: string;
  relatedMilestoneId?: string;
  relatedContactId?: string;
  fromUserId?: string;
  toUserId?: string;
  suggestedActionLabel: string;
  actionPath?: string;
}

export interface DeriveInterventionsParams {
  goals: Goal[];
  tasks: Task[];
  mgmt: ManagementSummary | undefined;
  rebalancing: TeamRebalancingResult | undefined;
  weekStart: string;
  weekEnd: string;
  maxCount?: number;
}

export function deriveInterventions({
  goals,
  tasks,
  mgmt,
  rebalancing,
  weekStart,
  weekEnd,
  maxCount = 5,
}: DeriveInterventionsParams): Intervention[] {
  const today = new Date().toISOString().split('T')[0];
  const in48hStr = (() => {
    const d = new Date();
    d.setHours(d.getHours() + 48);
    return d.toISOString().split('T')[0];
  })();

  const result: Intervention[] = [];

  // ── Rule 1: GOAL AT RISK ──────────────────────────────────
  // Behind goal with overdue/blocked linked work or significant pace gap.
  for (const goal of goals) {
    if (result.length >= maxCount) break;
    if (goal.progress.trackingStatus !== 'behind') continue;
    const exec = goal.executionSummary;
    const badPace = goal.progress.paceGap != null && goal.progress.paceGap < -10;
    if (!exec && !badPace) continue;
    if (exec && exec.overdueTasks === 0 && exec.blockedTasks === 0 && !badPace) continue;

    const parts: string[] = [];
    if (badPace) parts.push(`${Math.abs(goal.progress.paceGap!).toFixed(0)}% behind pace`);
    if (exec?.overdueTasks) parts.push(`${exec.overdueTasks} overdue task${exec.overdueTasks > 1 ? 's' : ''}`);
    if (exec?.blockedTasks) parts.push(`${exec.blockedTasks} blocked`);

    result.push({
      id: `goal-risk-${goal.id}`,
      type: exec?.blockedTasks ? 'review' : 'focus',
      severity: 'high',
      title: `Resolve work blocking "${goal.title}"`,
      description: `Behind${parts.length > 0 ? ` with ${parts.join(', ')}` : ''} — linked tasks need immediate attention.`,
      reason: 'Behind-pace goals rarely recover without clearing overdue and blocked work first.',
      relatedGoalId: goal.id,
      suggestedActionLabel: 'Open goal',
      actionPath: '/goals',
    });
  }

  // ── Rule 5: TASK DUE WITHIN 48H AND NOT STARTED / BLOCKED ─
  // Near-deadline tasks with no progress are high-likelihood misses.
  const atRisk48h = tasks
    .filter((t) => {
      if (!t.dueDate) return false;
      const d = t.dueDate.split('T')[0];
      return d >= today && d <= in48hStr && (t.status === 'todo' || t.status === 'blocked');
    })
    .sort((a, b) => (a.dueDate ?? '').localeCompare(b.dueDate ?? ''));

  if (atRisk48h.length > 0 && result.length < maxCount) {
    const t = atRisk48h[0];
    const dueLabel = t.dueDate!.split('T')[0] === today ? 'today' : 'tomorrow';
    result.push({
      id: `focus-48h-${t.id}`,
      type: 'focus',
      severity: 'high',
      title: `Focus now: "${t.title}" due ${dueLabel}`,
      description: `${t.status === 'blocked' ? 'Blocked and' : 'Not started,'} due ${dueLabel}.${atRisk48h.length > 1 ? ` +${atRisk48h.length - 1} more at risk.` : ''}`,
      reason: 'Late starts on near-due tasks frequently result in missed deadlines.',
      relatedTaskId: t.id,
      suggestedActionLabel: 'Open task',
      actionPath: '/tasks',
    });
  }

  // ── Rule 3: BLOCKED TASK WITH KNOWN REASON ────────────────
  // Blockers with a stated reason are immediately actionable.
  const blockedWithReason = tasks.filter((t) => t.status === 'blocked' && !!t.blockerReason);
  if (blockedWithReason.length > 0 && result.length < maxCount) {
    const t = blockedWithReason[0];
    result.push({
      id: `unblock-${t.id}`,
      type: 'unblock',
      severity: blockedWithReason.length > 2 ? 'high' : 'medium',
      title: `Unblock "${t.title}"`,
      description: `Blocker: "${t.blockerReason}".${blockedWithReason.length > 1 ? ` +${blockedWithReason.length - 1} others blocked.` : ''}`,
      reason: 'Unresolved blockers cascade and delay dependent work.',
      relatedTaskId: t.id,
      suggestedActionLabel: 'Open task',
      actionPath: '/tasks',
    });
  }

  // ── Rule 4: OVERLOADED USER + UNDERLOADED TEAMMATE ────────
  // Reuse the existing rebalancing recommendation directly.
  const topRec = rebalancing?.recommendations?.[0];
  if (topRec && result.length < maxCount) {
    result.push({
      id: `reassign-${topRec.taskId}`,
      type: 'reassign',
      severity: 'medium',
      title: `Reassign "${topRec.taskTitle}"`,
      description: topRec.reason,
      reason: 'Rebalancing workload prevents burnout and keeps delivery velocity high.',
      relatedTaskId: topRec.taskId,
      fromUserId: topRec.fromUserId,
      toUserId: topRec.toUserId,
      suggestedActionLabel: 'View rebalancing',
      actionPath: '/team',
    });
  }

  // ── Rule 6: REOPENED TASK STILL OPEN ─────────────────────
  // Indicates the original completion was premature — review before pushing more.
  const reopenedOpen = tasks.filter((t) => t.status !== 'done' && !!t.reopenReason);
  if (reopenedOpen.length > 0 && result.length < maxCount) {
    const t = reopenedOpen[0];
    result.push({
      id: `review-reopen-${t.id}`,
      type: 'review',
      severity: 'medium',
      title: `Review why "${t.title}" was reopened`,
      description: `Reopened: "${t.reopenReason}".${reopenedOpen.length > 1 ? ` +${reopenedOpen.length - 1} more.` : ''}`,
      reason: 'Reopened tasks suggest the original approach failed — understanding why prevents a second slip.',
      relatedTaskId: t.id,
      suggestedActionLabel: 'Open task',
      actionPath: '/tasks',
    });
  }

  // ── Rule 2: MANY UNESTIMATED TASKS ───────────────────────
  // Large unestimated backlog makes capacity planning unreliable.
  const unestimated = tasks.filter((t) => t.status !== 'done' && !t.estimatedMinutes);
  if (unestimated.length > 5 && result.length < maxCount) {
    result.push({
      id: 'estimate-missing',
      type: 'estimate',
      severity: 'medium',
      title: `Estimate ${unestimated.length} untracked open tasks`,
      description: `${unestimated.length} open tasks have no time estimate, making load planning unreliable.`,
      reason: "Without estimates it's impossible to know if the team is over-committed.",
      suggestedActionLabel: 'View tasks',
      actionPath: '/tasks',
    });
  }

  // ── Rule 7: OVERCOMMIT — too many tasks due this week ─────
  // Due tasks + existing overdue backlog signals the team cannot deliver everything.
  const dueThisWeekCount = tasks.filter(
    (t) =>
      t.status !== 'done' &&
      !!t.dueDate &&
      t.dueDate.split('T')[0] >= weekStart &&
      t.dueDate.split('T')[0] <= weekEnd,
  ).length;
  const existingOverdue = mgmt?.gaps.overdueTasks ?? 0;
  const teamSize = mgmt?.capacity.length ?? 0;
  if (teamSize > 0 && dueThisWeekCount > teamSize * 4 && existingOverdue > 0 && result.length < maxCount) {
    result.push({
      id: 'defer-overcommit',
      type: 'defer',
      severity: 'medium',
      title: 'Consider deferring lower-priority work',
      description: `${dueThisWeekCount} tasks due this week with ${existingOverdue} already overdue — the team may be over-committed.`,
      reason: 'Overcommitting leads to more slippage, growing the overdue backlog further next week.',
      suggestedActionLabel: 'Review tasks',
      actionPath: '/tasks',
    });
  }

  return result.slice(0, maxCount);
}

// ============================================================
// Project / Milestone interventions (deterministic, explainable)
// ============================================================

export interface DeriveProjectInterventionsParams {
  /** Projects enriched with progress + executionSummary + milestones (each enriched too). */
  projects: Project[];
  /** All workspace tasks — used for stalled-project recency (task.completedAt). */
  tasks: Task[];
  /** Today as YYYY-MM-DD. */
  today: string;
  /** Milestone "due soon" window in days. */
  dueSoonDays?: number;
  /** A project is "stalled" if no task completed within this many days. */
  stalledDays?: number;
  /** Project is at risk when project-wide overdue tasks exceed this. */
  overdueThreshold?: number;
  /** "Near end date" window (days) for the remaining-work-near-deadline rule. */
  nearEndDays?: number;
  /** Remaining advancement units considered "high" near the end date. */
  highRemaining?: number;
  maxCount?: number;
}

const SEVERITY_RANK: Record<InterventionSeverity, number> = { high: 0, medium: 1, low: 2 };

function daysUntil(dateStr: string, today: string): number {
  const a = new Date(`${today}T00:00:00`).getTime();
  const b = new Date(`${dateStr.split('T')[0]}T00:00:00`).getTime();
  return Math.round((b - a) / 86_400_000);
}

function completedWithinDays(dateStr: string | null | undefined, days: number, today: string): boolean {
  if (!dateStr) return false;
  const d = daysUntil(dateStr, today); // negative = in the past
  return d <= 0 && d >= -days;
}

/**
 * Deterministic project/milestone interventions. Every item carries a concrete,
 * human-readable `reason`. No LLM, no scoring models. Returns highest-severity
 * items first, capped at maxCount.
 */
export function deriveProjectInterventions({
  projects,
  tasks,
  today,
  dueSoonDays = 3,
  stalledDays = 14,
  overdueThreshold = 3,
  nearEndDays = 7,
  highRemaining = 5,
  maxCount = 8,
}: DeriveProjectInterventionsParams): Intervention[] {
  const result: Intervention[] = [];
  const isActive = (s: string) => s !== 'completed' && s !== 'cancelled';

  for (const project of projects) {
    if (!isActive(project.status)) continue;
    const exec = project.executionSummary;
    const prog = project.progress;
    const milestonesAtRisk = new Set<string>();

    // ── Rule 1: MILESTONE AT RISK ──────────────────────────
    for (const m of project.milestones ?? []) {
      if (m.status === 'completed') continue;
      const mExec = m.executionSummary;
      const behind = m.progress?.trackingStatus === 'behind';
      const overdue = (mExec?.overdueTasks ?? 0) > 0;
      const blocked = (mExec?.blockedTasks ?? 0) > 0;
      if (!behind && !overdue && !blocked) continue;

      milestonesAtRisk.add(m.id);
      const parts: string[] = [];
      if (behind) parts.push('behind pace');
      if (overdue) parts.push(`${mExec!.overdueTasks} overdue`);
      if (blocked) parts.push(`${mExec!.blockedTasks} blocked`);
      result.push({
        id: `milestone-risk-${m.id}`,
        type: blocked ? 'unblock' : 'review',
        severity: 'high',
        title: `Milestone at risk: "${m.name}"`,
        description: `In project "${project.name}" — ${parts.join(', ')}.`,
        reason: 'Milestones are the primary unit of project advancement; clearing blocked and overdue work keeps the project on track.',
        relatedProjectId: project.id,
        relatedMilestoneId: m.id,
        suggestedActionLabel: 'Open project',
        actionPath: '/projects',
      });
    }

    // ── Rule 2: PROJECT AT RISK ────────────────────────────
    const overdueTasks = exec?.overdueTasks ?? 0;
    const remaining = exec?.remainingWorkItems ?? 0;
    const nearEnd = !!project.endDate && daysUntil(project.endDate, today) <= nearEndDays && daysUntil(project.endDate, today) >= 0;
    const behindPace = prog?.trackingStatus === 'behind';
    const remainingHeavyNearEnd = nearEnd && remaining > highRemaining;
    if (behindPace || overdueTasks > overdueThreshold || remainingHeavyNearEnd) {
      const parts: string[] = [];
      if (behindPace) parts.push('behind pace');
      if (overdueTasks > overdueThreshold) parts.push(`${overdueTasks} overdue tasks`);
      if (remainingHeavyNearEnd) parts.push(`${remaining} work items left with ${daysUntil(project.endDate!, today)}d to deadline`);
      result.push({
        id: `project-risk-${project.id}`,
        type: (exec?.blockedTasks ?? 0) > 0 ? 'review' : 'focus',
        severity: 'high',
        title: `Project at risk: "${project.name}"`,
        description: parts.join(', ') + '.',
        reason: 'Projects behind pace or with mounting overdue work rarely recover without focused attention.',
        relatedProjectId: project.id,
        suggestedActionLabel: 'Open project',
        actionPath: '/projects',
      });
    }

    // ── Rule 3: STALLED PROJECT ────────────────────────────
    // Active project with remaining work but no task completed within stalledDays.
    if (project.status === 'active' && remaining > 0) {
      const projectTasks = tasks.filter((t) => t.projectId === project.id);
      const ageDays = -daysUntil(project.createdAt, today); // positive = days old
      const recentlyCompleted = projectTasks.some((t) => completedWithinDays(t.completedAt, stalledDays, today));
      if (ageDays >= stalledDays && !recentlyCompleted) {
        result.push({
          id: `project-stalled-${project.id}`,
          type: 'review',
          severity: 'medium',
          title: `Stalled project: "${project.name}"`,
          description: `No work completed in the last ${stalledDays} days, ${remaining} work item${remaining > 1 ? 's' : ''} remaining.`,
          reason: 'A project with open work but no recent completions is likely blocked, deprioritized, or forgotten.',
          relatedProjectId: project.id,
          suggestedActionLabel: 'Open project',
          actionPath: '/projects',
        });
      }
    }

    // ── Rule 4: MILESTONE DUE SOON ─────────────────────────
    for (const m of project.milestones ?? []) {
      if (m.status === 'completed' || !m.dueDate) continue;
      if (milestonesAtRisk.has(m.id)) continue; // already surfaced as at-risk
      const d = daysUntil(m.dueDate, today);
      if (d >= 0 && d <= dueSoonDays) {
        result.push({
          id: `milestone-due-${m.id}`,
          type: 'focus',
          severity: 'medium',
          title: `Milestone due ${d === 0 ? 'today' : d === 1 ? 'tomorrow' : `in ${d} days`}: "${m.name}"`,
          description: `In project "${project.name}" — due ${new Date(m.dueDate).toLocaleDateString()}.`,
          reason: 'Near-due milestones with open work are the most common cause of slipped project deadlines.',
          relatedProjectId: project.id,
          relatedMilestoneId: m.id,
          suggestedActionLabel: 'Open project',
          actionPath: '/projects',
        });
      }
    }
  }

  return result
    .sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity])
    .slice(0, maxCount);
}
